import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Button, Typography, CircularProgress } from "@mui/material";
import usePostRequest from "../../hooks/usePostRequest";
import useToast from "../../hooks/useToast";
import { SLATE } from "../../constants/Colors";

const FileUpload = ({ field, onChange, accept = "", endpoint, imageLabelStyleVariant="h6" , buttonVariant="outlined"}) => {
  const { error: toastError } = useToast();
  const tokens = useSelector((state) => state?.tokens.value);
  const fileInputRef = useRef();

  const [isImageValid, setIsImageValid] = useState(false);
  const [imgUploadedValue, setImgUploadedValue] = useState("");
  const [currentImage, setCurrentImage] = useState("");

  const { loading: uploading, sendPostRequest } = usePostRequest(
    endpoint,
    tokens
  );

  useEffect(() => {
    const validateImage = (url) => {
      //console.log("in validateImage: ", url)
      if (!url) {
        setIsImageValid(false);
        return;
      }
      const img = new Image();
      img.src = url;

      img.onload = () => {
        setIsImageValid(true);
        setCurrentImage(url); // Set the currently validated image
      };
      img.onerror = () => setIsImageValid(false); // Image failed to load
    };

    // Check the uploaded image value first, otherwise fallback to default value
    if (imgUploadedValue) {
      validateImage(imgUploadedValue);
    } else if (field?.defaultValue) {
      validateImage(field.defaultValue);
    } else {
      setIsImageValid(false); // No URL provided
    }
  }, [field.defaultValue, imgUploadedValue]);


  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const formData = new FormData();
    files.forEach((file) => {
      if(field.file_name){
        formData.append("files[]", file, field.file_name);
      }else{
        formData.append("files[]", file);
      }
      
    });

    try {
      const response = await sendPostRequest(formData);

      const uploadedFileUrls = response.files.map((file) => file.url);
      setImgUploadedValue("");
      setIsImageValid(false);
      setTimeout(() => setImgUploadedValue(uploadedFileUrls[0]), 0);
    
      //console.log(uploadedFileUrls)
      // Update the form state immediately
      onChange({
        target: {
          name: field.name,
          value: uploadedFileUrls,
        },
      });
    } catch (error) {
      console.error("Failed to upload files", error);
      //console.log(error)
      //alert("Failed to upload file. Please try again.");
      toastError(error.message)
    }
  };

  return (
    <div className="form-field">
      <Typography sx={{ fontSize: "0.875rem", color: SLATE[700], flexShrink: 0, maxWidth: "50%" }}>{field.label}</Typography>
      <input
        type="file"
        multiple={field.multiple}
        accept={accept}
        onChange={handleFileChange}
        ref={fileInputRef}
        style={{ display: "none" }}
      />
      <Button
        variant={buttonVariant}
        className="upload-button"
        onClick={handleButtonClick}
        disabled={uploading}
      >
        {isImageValid ? (
          <img
            src={currentImage}
            alt="Loaded content"
            height={"36.5px"}
          />
        ) : uploading ? (
          <CircularProgress size={24} />
        ) : (
          field.title || "Upload"
        )}
      </Button>
    </div>
  );
};

export default FileUpload;
